import type { ReactElement, ReactNode } from 'react';
import { SearchField as AriaSearchField, type SearchFieldProps } from 'react-aria-components';
import { useIntl } from 'react-intl';
import { Button } from '#components/button/button';
import { Spinner } from '#components/spinner/spinner';
import { CloseIcon } from './icon/close-icon';
import { SearchIcon } from './icon/search-icon';

export interface SearchFieldBaseProps extends Omit<SearchFieldProps, 'children'> {
  placeholder: string;
  /** The input element to render (e.g. DSK `Input` for the regular variant, RAC `Input` for toolbar). */
  inputSlot: ReactNode;
  /** Icon rendered inside the clear button. Defaults to the circle-with-X `CloseIcon`. */
  closeIcon?: ReactNode;
  iconClassName?: string;
  closeClassName?: string;
  /** Shows a loading spinner (positioned absolutely by the consuming variant's CSS). */
  showLoading?: boolean;
  spinnerClassName?: string;
}

export function SearchFieldBase({
  placeholder,
  className,
  inputSlot,
  closeIcon = <CloseIcon aria-hidden />,
  iconClassName,
  closeClassName,
  showLoading,
  spinnerClassName,
  ...props
}: SearchFieldBaseProps): ReactElement {
  const intl = useIntl();
  const clearLabel = intl.formatMessage({ id: 'a11y-buttons-clear-search-aria-label' });

  return (
    <AriaSearchField aria-label={placeholder} className={className} {...props}>
      <SearchIcon aria-hidden className={iconClassName} />
      {inputSlot}
      <Button
        aria-label={clearLabel}
        className={closeClassName}
        data-test-search-input-delete
        iconOnly
        size="small"
        variant="tertiary"
      >
        {closeIcon}
      </Button>
      {showLoading ? <Spinner className={spinnerClassName} /> : null}
    </AriaSearchField>
  );
}
